import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {DataTableComponent, Selectability, TableConfig} from '../../components/data-table/data-table.component';
import {StringFilterComponent} from "../../components/data-table/filters/string-filter/string-filter.component";
import {DateFilterComponent} from "../../components/data-table/filters/date-filter/date-filter.component";
import {firstValueFrom} from "rxjs";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {MessageDialogComponent} from "./dialog/message-dialog.component";
import {NumberFilterComponent} from "../../components/data-table/filters/number-filter/number-filter.component";
import {ActivatedRoute} from "@angular/router";
import {DataTableFilterComponent, FilterEntry} from "../../components/data-table/filters/data-table-filter.component";
import {FilterUtils} from "../../components/data-table/filters/filter";
import {Message, MessagesV1Service, Task, TaskStatus, TasksV1Service, TaskType} from "../../apis/backend";
import {FilterOption} from "../../utils/types";
import {UserService} from "../../services/user.service";
import {TaskDialogComponent} from "../tasks/dialog/task-dialog.component";

@Component({
  selector: 'message',
  templateUrl: './message.component.html',
})
export class MessageComponent implements AfterViewInit {
  
  public TableConfig: TableConfig = {
    ODataResourceName: 'messages',
    Selectability: Selectability.SingleSelect,
    Columns: [
      {
        DisplayName: 'ID',
        PropertyName: 'MessageID',
        FilterType: NumberFilterComponent
      },
      {
        DisplayName: 'Caller Name',
        PropertyName: 'CallerName',
        FilterType: StringFilterComponent
      },
      {
        DisplayName: 'Caller Company',
        PropertyName: 'CallerCompany',
        FilterType: StringFilterComponent
      },
      {
        DisplayName: 'Caller Phone Number',
        PropertyName: 'CallerPhoneNumber',
        FilterType: StringFilterComponent
      },
      {
        DisplayName: 'Received By',
        PropertyName: 'RecipientEmployee',
        Formatter: (v) => v ? v.FirstName + ' ' + v.LastName : ''
      },
      {
        DisplayName: 'Intended For',
        PropertyName: 'IntendedForEmployee',
        Formatter: (v) => v ? v.FirstName + ' ' + v.LastName : ''
      },
      {
        DisplayName: 'Call Received Date',
        PropertyName: 'CallReceivedDate',
        FilterType: DateFilterComponent,
        Formatter: (v) => new Date(v).toLocaleString('en-US', <any>{
          dateStyle: 'short',
          timeStyle: 'short'
        })
      },
      {
        DisplayName: 'Message',
        PropertyName: 'MessageText',
        FilterType: StringFilterComponent,
      },
      {
        DisplayName: 'Addressed?',
        PropertyName: 'IsAddressed',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Urgent?',
        PropertyName: 'IsUrgent',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Telephoned',
        PropertyName: 'Telephoned',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Called To See You',
        PropertyName: 'CalledToSeeYou',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Wants To See You',
        PropertyName: 'WantsToSeeYou',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Returned Your Call',
        PropertyName: 'ReturnedYourCall',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Please Call',
        PropertyName: 'PleaseCall',
        Formatter: (v) => v ? 'Yes' : 'No'
      },
      {
        DisplayName: 'Will Call Again',
        PropertyName: 'WillCallAgain',
        Formatter: (v) => v ? 'Yes' : 'No'
      }
    ]
  }
  
  public PrefilterTemplates: FilterOption[] = [
    {
      Text: 'My Messages (Active)',
      Value: 'IntendedForEmployee/EmployeeID eq {0} and IsAddressed eq false'
    },
    {
      Text: 'My Messages (All)',
      Value: 'IntendedForEmployee/EmployeeID eq {0}'
    },
    {
      Text: 'All Messages (Active)',
      Value: 'IsAddressed eq false'
    },
    {
      Text: 'All Messages',
      Value: ''
    }
  ];
  
  public Prefilters = this.PrefilterTemplates;
  
  public Prefilter: string;
  public Filter: string;
  
  public InitialFilters: FilterEntry[] = [];

  public PageIndex: number;
  public PageCount: number;
  
  @ViewChild(DataTableComponent)
  public DataTable: DataTableComponent;

  @ViewChild(DataTableFilterComponent)
  public DataTableFilter: DataTableFilterComponent;

  public Ready = false;
  
  constructor(
      private _messageService: MessagesV1Service,
      private _modalService: NgbModal,
      private _route: ActivatedRoute,
      private _taskService: TasksV1Service,
      private _userService: UserService) {

    _userService.CurrentUser.subscribe({
      next: (user) => {
        if (user != null) {
          this.Prefilters = this.PrefilterTemplates.map((f => ({
            Text: f.Text,
            Value: FilterUtils.format(f.Value, user.UserID)
          })));
        }
      }
    });
    this.Prefilter = this.Prefilters[0].Value;
    
    let messageID = this._route.snapshot.queryParams.messageID;
    if (messageID) {
      this.InitialFilters.push({
        Column: this.TableConfig.Columns.find(c => c.PropertyName == 'MessageID'),
        FilterSettings: NumberFilterComponent.ExactMatch(messageID)
      });
    }
  }

  ngAfterViewInit() {
    Promise.resolve(null).then(() => {
      this.Ready = true;
    })
  }

  public CreateMessage() {
    this._modalService.open(MessageDialogComponent, { size: 'lg' }).result
        .then((message: Message) => {
          return firstValueFrom(this._messageService.createMessage(message));
        })
        .then(() => {
          this.DataTable.Refresh();
        });
  }

  public EditMessage(message?: Message) {
    if (!message) {
      message = this.DataTable.GetSelection();
    }
    if (message) {
      message = <Message>JSON.parse(JSON.stringify(message));
      let modal = this._modalService.open(MessageDialogComponent, { size: 'lg' });
      modal.componentInstance.Message = message;

      modal.result
          .then((message: Message) => {
            return firstValueFrom(this._messageService.updateMessage(message.MessageID, message));
          })
          .then(() => {
            this.DataTable.Refresh();
          });
    }
  }
  
  public ConvertMessageToTask(message?: Message) {
    if (!message) {
      message = this.DataTable.GetSelection();
    }
    if (message) {
      message = <Message>JSON.parse(JSON.stringify(message));
      
      let task: Task = {
        Name: "Call From " + message.CallerName,
        CompanyName: message.CallerCompany,
        AssignedToEmployee: message.IntendedForEmployee,
        TaskType: {
          TaskTypeID: 3, // Follow-Up
          Name: 'Follow-Up'
        },
        TaskStatus: TaskStatus.New,
        Notes: message.MessageText
      };
      
      let modal = this._modalService.open(TaskDialogComponent, { size: 'lg' });
      modal.componentInstance.Task = task;

      modal.result
          .then((task: Task) => {
            if (task.TaskID) {
              return firstValueFrom(this._taskService.updateTask(task.TaskID, task));
            }
            else {
              return firstValueFrom(this._taskService.createTask(task));
            }
          })
          .then(() => {
            message.IsAddressed = true;
            return firstValueFrom(this._messageService.updateMessage(message.MessageID, message));
          })
          .then(() => {
            this.DataTable.Refresh();
          });
    }
  }
}
